import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Paper,
  Grid,
  Box,
  Card,
  CardContent,
  Button,
  Avatar,
  CircularProgress,
  TextField,
  IconButton,
  LinearProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Schedule as FileClockIcon,
  CheckCircle as FileCheckIcon,
  Refresh as RefreshIcon,
  Edit as Edit3Icon,
  Layers as LayersIcon,
  Business as Building2Icon,
  Warning as AlertTriangleIcon,
  ChevronRight as ChevronRightIcon,
} from "@material-ui/icons";
import { useAuth } from "../context/ApiKeyContext";
import { useHistory } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import { getApiBaseUrl } from "../config";
import { useWizard } from "../wizard/WizardContext";

const API_BASE_URL = getApiBaseUrl();

const DASHBOARD_INDICATORS_THROTTLE_MS = 10000; // mínimo 10s entre chamadas
const SESSION_KEY_LAST_FETCH = "dashboard_indicators_last_fetch";

// Converte dd/MM/yyyy para yyyy-MM-dd
function brDateToISO(brDate) {
  if (!brDate || brDate.length !== 10) return "";
  const [day, month, year] = brDate.split("/");
  return `${year}-${month}-${day}`;
}

// Converte yyyy-MM-dd para dd/MM/yyyy
function isoDateToBR(isoDate) {
  if (!isoDate || isoDate.length !== 10) return "";
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
}

// Aplica máscara dd/MM/yyyy
function applyDateMask(value) {
  // Remove tudo que não é número
  const numbers = value.replace(/\D/g, "");

  // Aplica máscara progressiva
  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 4) {
    return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
  } else {
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(
      4,
      8,
    )}`;
  }
}

function getMonthRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const first = `${y}-${m}-01`;
  const last = `${y}-${m}-${String(now.getDate()).padStart(2, "0")}`;
  return { date_start: first, date_end: last };
}

function getDateRange(type) {
  const now = new Date();
  const today = `${String(now.getDate()).padStart(2, "0")}/${String(
    now.getMonth() + 1,
  ).padStart(2, "0")}/${now.getFullYear()}`;

  switch (type) {
    case "today":
      return { date_start: today, date_end: today };
    case "yesterday": {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = `${String(yesterday.getDate()).padStart(
        2,
        "0",
      )}/${String(yesterday.getMonth() + 1).padStart(
        2,
        "0",
      )}/${yesterday.getFullYear()}`;
      return { date_start: yesterdayStr, date_end: yesterdayStr };
    }
    case "last7Days": {
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const startStr = `${String(sevenDaysAgo.getDate()).padStart(
        2,
        "0",
      )}/${String(sevenDaysAgo.getMonth() + 1).padStart(
        2,
        "0",
      )}/${sevenDaysAgo.getFullYear()}`;
      return { date_start: startStr, date_end: today };
    }
    case "last30Days": {
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const startStr = `${String(thirtyDaysAgo.getDate()).padStart(
        2,
        "0",
      )}/${String(thirtyDaysAgo.getMonth() + 1).padStart(
        2,
        "0",
      )}/${thirtyDaysAgo.getFullYear()}`;
      return { date_start: startStr, date_end: today };
    }
    case "all":
      // Para "todos", não filtrar por data
      return { date_start: "", date_end: "" };
    default:
      const monthRange = getMonthRange();
      return {
        date_start: isoDateToBR(monthRange.date_start),
        date_end: isoDateToBR(monthRange.date_end),
      };
  }
}


/** Prioridade por dias desde insertion: >=7 urgente, >=5 alta, >=2 normal, else baixa */
function getPriorityFromInsertion(job) {
  // Tenta usar initial_timestamp (quando começou processamento) ou insertion_timestamp (quando foi criado)
  const timestamp = job.initial_timestamp || job.insertion_timestamp;
  if (!timestamp) return { level: "baixa", days: 0, hours: 0 };
  const inserted = new Date(timestamp);
  const now = new Date();
  const hours = Math.floor((now - inserted) / (60 * 60 * 1000));
  const days = Math.floor(hours / 24);

  // Prioridade baseada em horas (>24h é o novo critério)
  if (hours >= 24) return { level: "alta", days, hours };
  return { level: "baixa", days, hours };
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  welcomeCard: {
    background:
      "linear-gradient(135deg, #7c3aed 0%, #9333ea 50%, #a855f7 100%)",
    color: "#fff",
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2.5),
    borderRadius: 12,
  },
  welcomeContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 12,
    backgroundColor: "rgba(255,255,255,0.2)",
    color: "#fff",
    fontSize: "1.25rem",
    fontWeight: 700,
  },
  welcomeSubtitle: {
    color: "rgba(233, 213, 255, 1)",
    fontSize: "0.875rem",
  },
  welcomeDate: {
    color: "rgba(233, 213, 255, 1)",
    fontSize: "0.875rem",
  },
  welcomeCompany: {
    color: "rgba(216, 180, 254, 1)",
    fontSize: "0.75rem",
    marginTop: 4,
  },
  tabsWrapper: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  tabsBox: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: "6px 8px",
    height: 42,
    minHeight: 42,
    boxSizing: "border-box",
    backgroundColor: "var(--ikaro-bg-surface)",
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid var(--ikaro-border-default)",
  },
  filterBox: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: "6px 8px",
    height: 42,
    minHeight: 42,
    boxSizing: "border-box",
    backgroundColor: "var(--ikaro-bg-surface)",
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid var(--ikaro-border-default)",
  },
  filterDateField: {
    "& .MuiInputBase-root": {
      fontSize: "0.8125rem",
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "0.75rem",
    },
    "& .MuiOutlinedInput-input": {
      padding: "4px 8px",
      height: 20,
      boxSizing: "content-box",
    },
    "& .MuiInputBase-root.MuiOutlinedInput-root": {
      height: 30,
      minHeight: 30,
      maxHeight: 30,
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      transform: "translate(12px, -7px) scale(0.75)",
    },
  },
  quickFilterButton: {
    fontSize: "0.7rem",
    padding: "4px 8px",
    minWidth: "auto",
    textTransform: "none",
    borderRadius: 6,
    border: "1px solid var(--ikaro-border-default)",
    backgroundColor: "var(--ikaro-bg-surface)",
    color: "var(--ikaro-text-secondary)",
    "&:hover": {
      backgroundColor: "var(--ikaro-bg-surface-hover)",
      borderColor: theme.palette.primary.main,
    },
  },
  quickFilterButtonActive: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  tabButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: "8px 16px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: 500,
    transition: "all 0.2s",
    color: "var(--ikaro-text-secondary)",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "var(--ikaro-bg-surface-hover)",
    },
  },
  tabButtonActive: {
    background: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)",
    color: "#fff",
    boxShadow: "0 2px 4px rgba(234, 88, 12, 0.3)",
    "&:hover": {
      backgroundColor: "transparent",
      opacity: 0.95,
    },
  },
  tabDescription: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    marginLeft: 4,
    width: "100%",
  },
  statsCard: {
    textAlign: "center",
    padding: theme.spacing(2),
  },
  statsNumber: {
    fontSize: "2.5rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  statsLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  indicatorCard: {
    height: "100%",
    padding: theme.spacing(2.5),
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
  },
  indicatorIconWrap: {
    display: "inline-flex",
    padding: theme.spacing(1.25),
    borderRadius: 8,
  },
  indicatorIconAmber: {
    backgroundColor: "rgba(245, 158, 11, 0.12)",
    color: "#d97706",
  },
  indicatorIconGreen: {
    backgroundColor: "rgba(34, 197, 94, 0.12)",
    color: "#16a34a",
  },
  indicatorIconPurple: {
    backgroundColor: "rgba(124, 58, 237, 0.12)",
    color: "#7c3aed",
  },
  indicatorIconRed: {
    backgroundColor: "rgba(239, 68, 68, 0.12)",
    color: "#dc2626",
  },
  indicatorValue: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(1.5),
  },
  indicatorLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  indicatorSubtitle: {
    fontSize: "0.75rem",
    color: theme.palette.text.hint,
    marginTop: 4,
  },
  indicatorsLoading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 120,
    padding: theme.spacing(3),
  },
  indicatorsError: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.error.main,
  },
  recentActivityCard: {
    height: "100%",
  },
  activityList: {
    maxHeight: 300,
    overflow: "auto",
  },
  activityItem: {
    borderBottom: "1px solid var(--ikaro-border-default)",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  chipSuccess: {
    backgroundColor: "var(--ikaro-status-success)",
    color: "var(--ikaro-text-white)",
  },
  chipPending: {
    backgroundColor: "var(--ikaro-status-warning)",
    color: "var(--ikaro-text-white)",
  },
  pendingCard: {
    marginTop: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  pendingCardContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  pendingItemsWrapper: {
    flex: 1,
    overflowY: "auto",
    marginTop: theme.spacing(1),
  },
  pendingCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  pendingCardTitleWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  pendingCardTitle: {
    fontWeight: 600,
    fontSize: "1rem",
    margin: 0,
  },
  createContestacaoButton: {
    background: "linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)",
    color: "#fff",
    fontSize: "0.75rem",
    fontWeight: 600,
    padding: "6px 12px",
    borderRadius: 6,
    textTransform: "none",
    boxShadow: "0 2px 4px rgba(124, 58, 237, 0.3)",
    "&:hover": {
      background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)",
      boxShadow: "0 4px 8px rgba(124, 58, 237, 0.4)",
    },
  },
  pendingItem: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    border: "1px solid var(--ikaro-border-default)",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: "var(--ikaro-bg-surface)",
    "&:hover": {
      background: "var(--ikaro-bg-surface-hover)",
      borderColor: "#f97316",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    },
    "&:last-child": { marginBottom: 0 },
  },
  pendingItemContent: {
    flex: 1,
    minWidth: 0,
    textAlign: "left",
  },
  pendingChip: {
    fontSize: "0.75rem",
    fontWeight: 600,
    padding: "4px 8px",
    borderRadius: 4,
    display: "inline-block",
  },
  chipUrgente: { backgroundColor: "#fef2f2", color: "#dc2626" },
  chipAlta: { backgroundColor: "#fef3c7", color: "#f59e0b" },
  chipNormal: { backgroundColor: "var(--ikaro-bg-surface-secondary)", color: "var(--ikaro-text-secondary)" },
  chipBaixa: { backgroundColor: "var(--ikaro-bg-surface-secondary)", color: "var(--ikaro-text-muted)" },
  pendingItemMeta: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    textAlign: "left",
  },
  pendingItemTitle: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.5),
    textAlign: "left",
  },
  pendingItemCase: {
    fontSize: "0.75rem",
    color: theme.palette.text.hint,
    textAlign: "left",
  },
  pendingChevron: {
    color: "var(--ikaro-text-muted)",
    transition: "all 0.2s ease",
  },
  sectionTitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: "bold",
  },
  progressCard: {
    marginTop: 0,
    flex: 1,
  },
  progressCardTitle: {
    marginBottom: theme.spacing(2.5),
    fontWeight: 600,
    fontSize: "1rem",
  },
  progressItem: {
    marginBottom: theme.spacing(3),
    "&:last-child": { marginBottom: 0 },
  },
  progressHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  progressLabel: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  progressValue: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  progressBarCompleted: {
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#10b981", // verde
    },
    backgroundColor: "rgba(16, 185, 129, 0.1)",
    borderRadius: 4,
    height: 8,
  },
  progressBarPending: {
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#f59e0b", // amarelo/laranja
    },
    backgroundColor: "rgba(245, 158, 11, 0.1)",
    borderRadius: 4,
    height: 8,
  },
  userStatsCard: {
    marginTop: theme.spacing(2),
    flex: 1,
  },
  userStatsCardTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    fontSize: "1rem",
  },
  userStatsItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5),
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    borderRadius: 8,
    marginBottom: theme.spacing(1.5),
    "&:last-child": { marginBottom: 0 },
  },
  userStatsLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  userStatsValue: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  // Estilos para Gestor de Célula
  cellManagerTable: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  cellManagerTableHead: {
    "& th": {
      fontSize: "0.75rem",
      fontWeight: 500,
      color: theme.palette.text.secondary,
      padding: theme.spacing(1.5),
      textAlign: "left",
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  cellManagerTableBody: {
    "& tr": {
      transition: "background 0.2s",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "var(--ikaro-bg-surface-hover)",
      },
    },
    "& td": {
      fontSize: "0.875rem",
      padding: theme.spacing(1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  cellManagerUserAvatar: {
    width: 32,
    height: 32,
    fontSize: "0.75rem",
    fontWeight: 600,
    background:
      "linear-gradient(135deg, #7c3aed 0%, #9333ea 50%, #a855f7 100%)",
    color: "#fff",
    marginRight: theme.spacing(1.5),
  },
  cellManagerUserInfo: {
    display: "flex",
    alignItems: "center",
  },
  cellManagerUserName: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  cellManagerBadge: {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  cellManagerBadgeHigh: {
    backgroundColor: "var(--ikaro-status-error-bg)",
    color: "var(--ikaro-status-error-text)",
  },
  cellManagerBadgeNormal: {
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    color: "var(--ikaro-text-secondary)",
  },
  cellManagerTrend: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.75rem",
  },
  cellManagerTrendUp: {
    color: "#10b981",
  },
  cellManagerTrendDown: {
    color: "#ef4444",
  },
  cellManagerTrendStable: {
    color: "#9ca3af",
  },
  productivityCard: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    backgroundColor: "var(--ikaro-bg-surface)",
    borderRadius: 0,
    boxShadow: "none",
    border: "1px solid var(--ikaro-border-default)",
  },
  productivityCardTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "var(--ikaro-text-primary)",
    marginBottom: 20,
  },
  chartContainer: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 4,
    height: 200,
    padding: "0 4px",
  },
  barWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    cursor: "pointer",
    transition: "transform 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },
  bar: {
    width: "100%",
    backgroundColor: "#9333ea",
    borderRadius: "4px 4px 0 0",
    transition: "background-color 0.2s, opacity 0.2s",
    minHeight: 8,
    "&:hover": {
      backgroundColor: "#7c3aed",
      transform: "scaleY(1.05)",
      transformOrigin: "bottom",
    },
  },
  barLabel: {
    fontSize: "0.625rem",
    color: "var(--ikaro-text-muted)",
    textAlign: "center",
  },
  chartLegend: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 16,
    paddingTop: 16,
    borderTop: "1px solid var(--ikaro-border-default)",
  },
  legendItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  legendValue: {
    fontSize: "1.25rem",
    fontWeight: 600,
    color: "var(--ikaro-text-primary)",
  },
  legendLabel: {
    fontSize: "0.75rem",
    color: "var(--ikaro-text-secondary)",
    marginTop: 4,
  },
  // Estilos para Gestor de Escritório
  officeManagerCard: {
    padding: theme.spacing(4),
    borderRadius: 12,
    border: "1px solid var(--ikaro-border-default)",
    transition: "all 0.2s",
    cursor: "pointer",
    "&:hover": {
      borderColor: "#7c3aed",
      backgroundColor: "var(--ikaro-bg-surface-hover)",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    },
  },
  officeManagerCellIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    background:
      "linear-gradient(135deg, #7c3aed 0%, #9333ea 50%, #a855f7 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: "0.875rem",
    fontWeight: 700,
  },
  officeManagerMetric: {
    textAlign: "center",
  },
  officeManagerMetricValue: {
    fontSize: "1.125rem",
    fontWeight: 700,
  },
  officeManagerMetricLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  officeManagerProgressBar: {
    height: 6,
    backgroundColor: "var(--ikaro-bg-surface-secondary)",
    borderRadius: 3,
    overflow: "hidden",
    marginTop: theme.spacing(1.5),
  },
  officeManagerProgressFill: {
    height: "100%",
    background: "linear-gradient(90deg, #7c3aed 0%, #9333ea 100%)",
    borderRadius: 3,
  },
  clientLegendItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1),
    borderRadius: 8,
    cursor: "pointer",
    transition: "background 0.2s",
    "&:hover": {
      backgroundColor: "var(--ikaro-bg-surface-hover)",
    },
  },
  clientLegendColor: {
    width: 12,
    height: 12,
    borderRadius: 3,
    marginRight: theme.spacing(1),
  },
}));

// Componente de gráfico de pizza (donut chart)
const DonutChart = ({ data, size = 140 }) => {
  // Debug: verificar dados recebidos
  console.log("DonutChart - dados recebidos:", data);

  // Filtrar apenas itens com valor > 0
  const validData = data.filter((item) => item && item.value > 0);
  const total = validData.reduce((sum, item) => sum + item.value, 0);

  console.log("DonutChart - dados válidos:", validData, "total:", total);

  if (!data || data.length === 0) {
    return (
      <Box
        style={{
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#9ca3af",
          fontSize: "0.75rem",
        }}
      >
        Nenhum cliente
      </Box>
    );
  }

  if (total === 0 || validData.length === 0) {
    return (
      <Box
        style={{
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#9ca3af",
          fontSize: "0.75rem",
        }}
      >
        Sem volume no período
      </Box>
    );
  }

  // Se há apenas um item com 100%, desenhar um círculo completo
  if (validData.length === 1) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        style={{ overflow: "visible" }}
      >
        <circle
          cx="50"
          cy="50"
          r="40"
          fill={validData[0].color || "#cccccc"}
          stroke="var(--ikaro-bg-surface)"
          strokeWidth="1"
          className="dashboard-donut-slice"
        />
        <circle
          cx="50"
          cy="50"
          r="25"
          fill="var(--ikaro-bg-surface)"
          stroke="var(--ikaro-border-default)"
          strokeWidth="1"
        />
        <text
          x="50"
          y="48"
          textAnchor="middle"
          style={{
            fontSize: "14px",
            fontWeight: "700",
            fill: "var(--ikaro-text-primary)",
          }}
        >
          {total}
        </text>
        <text
          x="50"
          y="58"
          textAnchor="middle"
          style={{
            fontSize: "8px",
            fill: "var(--ikaro-text-secondary)",
          }}
        >
          Total
        </text>
      </svg>
    );
  }

  let currentAngle = -90;
  const paths = validData.map((item, index) => {
    const angle = (item.value / total) * 360;
    const startAngle = currentAngle;
    currentAngle += angle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = ((startAngle + angle) * Math.PI) / 180;

    const x1 = 50 + 40 * Math.cos(startRad);
    const y1 = 50 + 40 * Math.sin(startRad);
    const x2 = 50 + 40 * Math.cos(endRad);
    const y2 = 50 + 40 * Math.sin(endRad);

    const largeArcFlag = angle > 180 ? 1 : 0;

    console.log(`Fatia ${index}:`, {
      value: item.value,
      angle,
      color: item.color,
      path: `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2} Z`,
    });

    return (
      <path
        key={index}
        d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
        className="dashboard-donut-slice"
        style={{
          fill: item.color || "#cccccc",
          stroke: "var(--ikaro-bg-surface)",
          strokeWidth: 1,
        }}
      />
    );
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      style={{ overflow: "visible" }}
    >
      <g>{paths}</g>
      <circle
        cx="50"
        cy="50"
        r="25"
        fill="var(--ikaro-bg-surface)"
        stroke="var(--ikaro-border-default)"
        strokeWidth="1"
      />
      <text
        x="50"
        y="48"
        textAnchor="middle"
        style={{
          fontSize: "14px",
          fontWeight: "700",
          fill: "var(--ikaro-text-primary)",
        }}
      >
        {total}
      </text>
      <text
        x="50"
        y="58"
        textAnchor="middle"
        style={{
          fontSize: "8px",
          fill: "var(--ikaro-text-secondary)",
        }}
      >
        Total
      </text>
    </svg>
  );
};

const Dashboard = () => {
  const classes = useStyles();
  const { user, cell, hasRole } = useAuth();
  const history = useHistory();
  const { t, formatDate } = useTranslation("dashboard");
  const { sessions, startSession, refetch: refetchWizard } = useWizard();

  const [indicators, setIndicators] = useState(null);
  const [indicatorsLoading, setIndicatorsLoading] = useState(false);
  const [indicatorsError, setIndicatorsError] = useState(null);
  const [todayIndicators, setTodayIndicators] = useState(null); // Indicadores de hoje (fixo)
  const [weekIndicators, setWeekIndicators] = useState(null); // Indicadores da semana
  const [dailyProductivity, setDailyProductivity] = useState([]); // Produtividade dos últimos 30 dias
  const [dailyProductivityLoading, setDailyProductivityLoading] =
    useState(false);
  // Armazena datas em formato brasileiro (dd/MM/yyyy) para exibição
  const [dateStart, setDateStart] = useState(() =>
    isoDateToBR(getMonthRange().date_start),
  );
  const [dateEnd, setDateEnd] = useState(() =>
    isoDateToBR(getMonthRange().date_end),
  );
  const [activeProfile, setActiveProfile] = useState("user");
  const [pendingJobs, setPendingJobs] = useState([]);
  const [pendingJobsLoading, setPendingJobsLoading] = useState(false);
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);
  const initialFetchDone = useRef(false);
  const prevCellValue = useRef(undefined);

  // Estado para Gestor de Célula
  const [cellManagerStats, setCellManagerStats] = useState(null);
  const [cellManagerLoading, setCellManagerLoading] = useState(false);
  const [cellManagerError, setCellManagerError] = useState(null);
  const [hasCellManagerPermission, setHasCellManagerPermission] =
    useState(false);
  const [cellManagerFilter, setCellManagerFilter] = useState("thisMonth");
  const [managerPendingJobs, setManagerPendingJobs] = useState([]);
  const [managerPendingLoading, setManagerPendingLoading] = useState(false);

  // Estado para Gestor de Escritório
  const [officeManagerStats, setOfficeManagerStats] = useState(null);
  const [officeManagerLoading, setOfficeManagerLoading] = useState(false);
  const [officeManagerError, setOfficeManagerError] = useState(null);
  const [hasOfficeManagerPermission, setHasOfficeManagerPermission] =
    useState(false);
  const [officeManagerFilter, setOfficeManagerFilter] = useState("thisMonth");
  const [officeManagerCellFilter, setOfficeManagerCellFilter] = useState(false); // false = todos, true = célula atual

  const quickFilters = [
    { id: "today", label: t("indicators.today") },
    { id: "yesterday", label: t("indicators.yesterday") },
    { id: "last7Days", label: t("indicators.last7Days") },
    { id: "last30Days", label: t("indicators.last30Days") },
    { id: "all", label: t("indicators.all") },
  ];

  const profiles = [
    {
      id: "user",
      name: "Usuário",
      icon: Edit3Icon,
      description: "Criador/Revisor de contestações",
    },
    {
      id: "cell",
      name: "Gestor de Célula",
      icon: LayersIcon,
      description: "Gestão da equipe local",
    },
    {
      id: "office",
      name: "Gestor de Escritório",
      icon: Building2Icon,
      description: "Visão consolidada do escritório",
    },
  ];

  useEffect(() => {
    const authToken = localStorage.getItem("ikaro_auth_token");
    if (!authToken || initialFetchDone.current) return;
    initialFetchDone.current = true;

    refetchWizard();

    // Buscar indicadores apenas da aba de usuário
    if (activeProfile === "user") {
      fetchIndicators(true, null);
      fetchTodayIndicators(); // Busca indicadores de hoje separadamente
      fetchWeekIndicators(); // Busca indicadores da semana
      fetchDailyProductivity(); // Busca produtividade dos últimos 30 dias
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Disparar wizard para a primeira sessão não vista (só uma vez por carga)
  const wizardTriggered = useRef(false);
  useEffect(() => {
    if (!sessions.length || wizardTriggered.current) return;
    wizardTriggered.current = true;
    const unseen = sessions.find((s) => !s.completed && s.steps?.length > 0);
    if (unseen) startSession(unseen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  // Verificar permissão de gestor de célula ao carregar
  useEffect(() => {
    if (cell?.value) {
      // Verificar se tem a regra all_cells no serviço CELLS ou manager em DOCUMENT_CREATION
      const hasCellsAllCells = hasRole("CELLS", "all_cells");
      const hasManagerRule = hasRole("DOCUMENT_CREATION", "manager");

      console.log("Verificando permissões de gestor:", {
        hasCellsAllCells,
        hasManagerRule,
        cell: cell?.value,
      });

      setHasCellManagerPermission(hasCellsAllCells || hasManagerRule);
    } else {
      setHasCellManagerPermission(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cell, user]);

  // Verificar permissão de gestor de escritório ao carregar
  useEffect(() => {
    // Verificar se tem a regra office_manager no serviço CELLS
    const hasOfficeManager = hasRole("CELLS", "office_manager");

    console.log("Verificando permissão de gestor de escritório:", {
      hasOfficeManager,
    });

    setHasOfficeManagerPermission(hasOfficeManager);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Carregar estatísticas do gestor de célula quando mudar para essa aba
  useEffect(() => {
    if (activeProfile === "cell" && hasCellManagerPermission) {
      fetchCellManagerStatistics();
      fetchManagerPendingJobs();
      fetchDailyProductivity(); // Busca produtividade dos últimos 30 dias
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfile, cellManagerFilter, cell, hasCellManagerPermission]);

  // Carregar estatísticas do gestor de escritório quando mudar para essa aba
  useEffect(() => {
    if (activeProfile === "office" && hasOfficeManagerPermission) {
      fetchOfficeManagerStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeProfile,
    hasOfficeManagerPermission,
    officeManagerFilter,
    officeManagerCellFilter,
    cell,
  ]);

  // Recarregar dashboard ao trocar de célula
  useEffect(() => {
    const currentCellValue = cell?.value ?? null;
    if (prevCellValue.current === undefined) {
      prevCellValue.current = currentCellValue;
      return;
    }
    if (prevCellValue.current === currentCellValue) return;
    prevCellValue.current = currentCellValue;

    fetchIndicators(true, null);
    fetchTodayIndicators();
    fetchWeekIndicators();
    fetchDailyProductivity();
    if (activeProfile === "cell" && hasCellManagerPermission) {
      fetchCellManagerStatistics();
      fetchManagerPendingJobs();
    }
    if (activeProfile === "office" && hasOfficeManagerPermission) {
      fetchOfficeManagerStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cell?.value]);

  const fetchIndicators = (
    skipThrottle = false,
    cellIdOverride = null,
    dateStartOverride = null,
    dateEndOverride = null,
  ) => {
    const authToken = localStorage.getItem("ikaro_auth_token");
    if (!authToken) return;
    if (!hasRole("DOCUMENT_CREATION", "view_jobs")) return;

    if (!skipThrottle) {
      const now = Date.now();
      const lastStr = sessionStorage.getItem(SESSION_KEY_LAST_FETCH);
      const lastFetch = lastStr ? parseInt(lastStr, 10) : 0;
      if (now - lastFetch < DASHBOARD_INDICATORS_THROTTLE_MS) return;
      sessionStorage.setItem(SESSION_KEY_LAST_FETCH, String(now));
    }

    setIndicatorsError(null);
    setIndicatorsLoading(true);

    const effectiveCellId =
      cellIdOverride !== null && cellIdOverride !== undefined
        ? cellIdOverride
        : (cell?.value ?? null);

    // Usar datas passadas como parâmetro ou do estado
    const effectiveDateStart =
      dateStartOverride !== null ? dateStartOverride : dateStart;
    const effectiveDateEnd =
      dateEndOverride !== null ? dateEndOverride : dateEnd;

    const params = new URLSearchParams({
      scope: "me",
    });
    // Converte datas do formato brasileiro (dd/MM/yyyy) para ISO (yyyy-MM-dd) antes de enviar
    if (effectiveDateStart) {
      const isoStart = brDateToISO(effectiveDateStart);
      if (isoStart) params.append("date_start", isoStart);
    }
    if (effectiveDateEnd) {
      const isoEnd = brDateToISO(effectiveDateEnd);
      if (isoEnd) params.append("date_end", isoEnd);
    }
    if (effectiveCellId != null && effectiveCellId !== "") {
      params.append("cell_id", String(effectiveCellId));
    }

    fetch(`${API_BASE_URL}/job/get_dashboard_indicators?${params}`, {
      method: "GET",
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${authToken}`,
        ServiceName: "DOCUMENT_CREATION",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setIndicators(data);
        fetchPendingJobs(effectiveDateStart, effectiveDateEnd);
      })
      .catch((err) => {
        setIndicatorsError(err.message || "Erro ao carregar indicadores");
        setIndicators(null);
      })
      .finally(() => {
        setIndicatorsLoading(false);
      });
  };

  const fetchTodayIndicators = () => {
    const authToken = localStorage.getItem("ikaro_auth_token");
    if (!authToken) return;
    if (!hasRole("DOCUMENT_CREATION", "view_jobs")) return;

    const effectiveCellId = cell?.value ?? null;
    const now = new Date();
    // Formato: YYYY-MM-DD para pegar de 00:00:00 até 23:59:59 do dia atual
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(now.getDate()).padStart(2, "0")}`;

    const params = new URLSearchParams({
      scope: "me",
      date_start: today, // Backend converte para 00:00:00
      date_end: today, // Backend converte para 23:59:59
    });
    if (effectiveCellId != null && effectiveCellId !== "") {
      params.append("cell_id", String(effectiveCellId));
    }

    console.log(
      "Buscando indicadores de hoje:",
      today,
      "cell_id:",
      effectiveCellId,
    );

    fetch(`${API_BASE_URL}/job/get_dashboard_indicators?${params}`, {
      method: "GET",
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${authToken}`,
        ServiceName: "DOCUMENT_CREATION",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        console.log("Indicadores de hoje recebidos:", data);
        setTodayIndicators(data);
      })
      .catch((err) => {
        console.error("Erro ao carregar indicadores de hoje:", err);
        setTodayIndicators(null);
      });
  };

  const fetchWeekIndicators = () => {
    const authToken = localStorage.getItem("ikaro_auth_token");
    if (!authToken) return;
    if (!hasRole("DOCUMENT_CREATION", "view_jobs")) return;

    const effectiveCellId = cell?.value ?? null;
    const now = new Date();
    // Últimos 7 dias
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const startWeek = `${sevenDaysAgo.getFullYear()}-${String(
      sevenDaysAgo.getMonth() + 1,
    ).padStart(2, "0")}-${String(sevenDaysAgo.getDate()).padStart(2, "0")}`;
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(now.getDate()).padStart(2, "0")}`;

    const params = new URLSearchParams({
      scope: "me",
      date_start: startWeek,
      date_end: today,
    });
    if (effectiveCellId != null && effectiveCellId !== "") {
      params.append("cell_id", String(effectiveCellId));
    }

    fetch(`${API_BASE_URL}/job/get_dashboard_indicators?${params}`, {
      method: "GET",
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${authToken}`,
        ServiceName: "DOCUMENT_CREATION",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setWeekIndicators(data);
      })
      .catch((err) => {
        console.error("Erro ao carregar indicadores da semana:", err);
        setWeekIndicators(null);
      });
  };

  const fetchDailyProductivity = async () => {
    const authToken = localStorage.getItem("ikaro_auth_token");
    if (!authToken) return;
    if (!hasRole("DOCUMENT_CREATION", "view_jobs")) return;

    setDailyProductivityLoading(true);
    const effectiveCellId = cell?.value ?? null;
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const dateStart = `${thirtyDaysAgo.getFullYear()}-${String(
      thirtyDaysAgo.getMonth() + 1,
    ).padStart(2, "0")}-${String(thirtyDaysAgo.getDate()).padStart(2, "0")}`;
    const dateEnd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(now.getDate()).padStart(2, "0")}`;

    const params = new URLSearchParams({
      scope: "me",
      date_start: dateStart,
      date_end: dateEnd,
    });
    if (effectiveCellId != null && effectiveCellId !== "") {
      params.append("cell_id", String(effectiveCellId));
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/job/get_daily_productivity?${params}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${authToken}`,
            ServiceName: "DOCUMENT_CREATION",
          },
        },
      );

      const data = await response.json();
      if (data.daily_productivity) {
        setDailyProductivity(data.daily_productivity);
      }
    } catch (err) {
      console.error("Erro ao carregar produtividade diária:", err);
      setDailyProductivity([]);
    } finally {
      setDailyProductivityLoading(false);
    }
  };

  const fetchPendingJobs = (dateStartParam, dateEndParam) => {
    const authToken = localStorage.getItem("ikaro_auth_token");
    if (!authToken) return;
    if (!hasRole("DOCUMENT_CREATION", "view_jobs")) return;
    setPendingJobsLoading(true);
    const ds = dateStartParam ?? dateStart;
    const de = dateEndParam ?? dateEnd;
    // Revisão pendente: task CONTESTATION_REVIEW em fila/execução (alinhado ao filtro por etapa)
    const params = new URLSearchParams({
      task_type_filter: "CONTESTATION_REVIEW",
      sort_by: "insertion_timestamp",
      sort_order: "asc",
      page_size: "3",
      source_type: "user",
      code: "DOCUMENT_CREATION",
      scope: "me",
    });
    if (cell?.value != null && cell?.value !== "") {
      params.append("cell_id", String(cell.value));
    }

    // Mesmo recorte de datas dos indicadores (insertion_timestamp no backend)
    if (ds) {
      const isoStart = brDateToISO(ds);
      if (isoStart) params.append("date_start", isoStart);
    }
    if (de) {
      const isoEnd = brDateToISO(de);
      if (isoEnd) params.append("date_end", isoEnd);
    }

    fetch(`${API_BASE_URL}/job/get_jobs_list?${params}`, {
      method: "GET",
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${authToken}`,
        ServiceName: "DOCUMENT_CREATION",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const pending = data.jobs || [];
        setPendingJobs(pending.slice(0, 3)); // Top 3 mais antigas
      })
      .catch(() => setPendingJobs([]))
      .finally(() => setPendingJobsLoading(false));
  };

  const getCellManagerDateRange = () => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(now.getDate()).padStart(2, "0")}`;

    switch (cellManagerFilter) {
      case "today": {
        return { date_start: today, date_end: today };
      }
      case "yesterday": {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = `${yesterday.getFullYear()}-${String(
          yesterday.getMonth() + 1,
        ).padStart(2, "0")}-${String(yesterday.getDate()).padStart(2, "0")}`;
        return { date_start: yesterdayStr, date_end: yesterdayStr };
      }
      case "thisWeek": {
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const startStr = `${sevenDaysAgo.getFullYear()}-${String(
          sevenDaysAgo.getMonth() + 1,
        ).padStart(2, "0")}-${String(sevenDaysAgo.getDate()).padStart(2, "0")}`;
        return { date_start: startStr, date_end: today };
      }
      case "thisMonth":
      default: {
        const firstDay = `${now.getFullYear()}-${String(
          now.getMonth() + 1,
        ).padStart(2, "0")}-01`;
        return { date_start: firstDay, date_end: today };
      }
    }
  };

  const fetchCellManagerStatistics = () => {
    const authToken = localStorage.getItem("ikaro_auth_token");
    if (!authToken || !cell?.value) return;

    setCellManagerLoading(true);
    setCellManagerError(null);

    const dateRange = getCellManagerDateRange();

    const params = new URLSearchParams({
      cell_id: String(cell.value),
    });

    if (dateRange.date_start) params.append("date_start", dateRange.date_start);
    if (dateRange.date_end) params.append("date_end", dateRange.date_end);

    fetch(`${API_BASE_URL}/job/get_cell_manager_statistics?${params}`, {
      method: "GET",
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${authToken}`,
        ServiceName: "DOCUMENT_CREATION",
      },
    })
      .then((res) => {
        if (res.status === 403) {
          setHasCellManagerPermission(false);
          throw new Error("Sem permissão");
        }
        if (!res.ok) throw new Error("Erro na requisição");
        setHasCellManagerPermission(true);
        return res.json();
      })
      .then((data) => {
        setCellManagerStats(data);
      })
      .catch((err) => {
        setCellManagerError(err.message);
        setCellManagerStats(null);
      })
      .finally(() => {
        setCellManagerLoading(false);
      });
  };

  const fetchManagerPendingJobs = () => {
    const authToken = localStorage.getItem("ikaro_auth_token");
    if (!authToken || !cell?.value) return;

    setManagerPendingLoading(true);

    // Revisão pendente na célula: task CONTESTATION_REVIEW em andamento
    const params = new URLSearchParams({
      task_type_filter: "CONTESTATION_REVIEW",
      sort_by: "insertion_timestamp",
      sort_order: "asc",
      page_size: "5",
      code: "DOCUMENT_CREATION",
      cell_id: String(cell.value),
    });

    fetch(`${API_BASE_URL}/job/get_jobs_list?${params}`, {
      method: "GET",
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${authToken}`,
        ServiceName: "DOCUMENT_CREATION",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const pending = data.jobs || [];
        setManagerPendingJobs(pending.slice(0, 5));
      })
      .catch(() => setManagerPendingJobs([]))
      .finally(() => setManagerPendingLoading(false));
  };

  const getOfficeManagerDateRange = () => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(now.getDate()).padStart(2, "0")}`;

    switch (officeManagerFilter) {
      case "today": {
        return { date_start: today, date_end: today };
      }
      case "yesterday": {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = `${yesterday.getFullYear()}-${String(
          yesterday.getMonth() + 1,
        ).padStart(2, "0")}-${String(yesterday.getDate()).padStart(2, "0")}`;
        return { date_start: yesterdayStr, date_end: yesterdayStr };
      }
      case "thisWeek": {
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const startStr = `${sevenDaysAgo.getFullYear()}-${String(
          sevenDaysAgo.getMonth() + 1,
        ).padStart(2, "0")}-${String(sevenDaysAgo.getDate()).padStart(2, "0")}`;
        return { date_start: startStr, date_end: today };
      }
      case "thisMonth":
      default: {
        const firstDay = `${now.getFullYear()}-${String(
          now.getMonth() + 1,
        ).padStart(2, "0")}-01`;
        return { date_start: firstDay, date_end: today };
      }
    }
  };

  const fetchOfficeManagerStatistics = () => {
    const authToken = localStorage.getItem("ikaro_auth_token");
    if (!authToken) return;

    setOfficeManagerLoading(true);
    setOfficeManagerError(null);

    const dateRange = getOfficeManagerDateRange();

    // Buscar todas as células de uma vez (sem paginação no backend)
    const params = new URLSearchParams({
      page: "1",
      page_size: "500", // Buscar todas as células (limite do backend)
    });

    if (dateRange.date_start) params.append("date_start", dateRange.date_start);
    if (dateRange.date_end) params.append("date_end", dateRange.date_end);
    if (officeManagerCellFilter && cell?.value) {
      params.append("cell_id", String(cell.value));
    }

    fetch(`${API_BASE_URL}/job/get_office_manager_statistics?${params}`, {
      method: "GET",
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${authToken}`,
        ServiceName: "DOCUMENT_CREATION",
      },
    })
      .then((res) => {
        if (res.status === 403) {
          setHasOfficeManagerPermission(false);
          throw new Error("Sem permissão");
        }
        if (!res.ok) throw new Error("Erro na requisição");
        setHasOfficeManagerPermission(true);
        return res.json();
      })
      .then((data) => {
        // Atualizar o estado com os dados completos
        setOfficeManagerStats(data);
      })
      .catch((err) => {
        setOfficeManagerError(err.message);
        setOfficeManagerStats(null);
      })
      .finally(() => {
        setOfficeManagerLoading(false);
      });
  };

  const getUserInitials = () => {
    if (user && user.name) {
      return user.name.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  const handleQuickFilter = (filterId) => {
    const range = getDateRange(filterId);
    setDateStart(range.date_start);
    setDateEnd(range.date_end);
    setActiveQuickFilter(filterId);
    // Passa as datas diretamente para evitar problema de estado assíncrono
    fetchIndicators(true, null, range.date_start, range.date_end);
    fetchTodayIndicators(); // Atualiza também os indicadores de hoje
    fetchWeekIndicators(); // Atualiza também os indicadores da semana
  };

  return (
    <div className={classes.root}>
      {/* Abas centralizadas */}
      <div className={classes.tabsWrapper}>
        <div className={classes.tabsBox}>
          {profiles
            .filter((p) => {
              // Sempre mostrar aba "user"
              if (p.id === "user") return true;
              // Mostrar aba "cell" apenas se tiver permissão
              if (p.id === "cell") return hasCellManagerPermission;
              // Mostrar aba "office" apenas se tiver permissão
              if (p.id === "office") return hasOfficeManagerPermission;
              return true;
            })
            .map((p) => {
              const Icon = p.icon;
              const isActive = activeProfile === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`${classes.tabButton} ${
                    isActive ? classes.tabButtonActive : ""
                  }`}
                  onClick={() => setActiveProfile(p.id)}
                >
                  <Icon style={{ fontSize: 18 }} />
                  <span>{p.name}</span>
                </button>
              );
            })}
        </div>
      </div>

      {/* Filtros embaixo das abas */}
      <div className="dashboard-filter-center">
        {activeProfile === "user" && (
          <div className={classes.filterBox}>
            {quickFilters.map((filter) => (
              <Button
                key={filter.id}
                className={`${classes.quickFilterButton} ${
                  activeQuickFilter === filter.id
                    ? classes.quickFilterButtonActive
                    : ""
                }`}
                onClick={() => handleQuickFilter(filter.id)}
              >
                {filter.label}
              </Button>
            ))}
            {activeQuickFilter !== "all" && (
              <>
                <Box className="dashboard-filter-divider" />
                <TextField
                  className={`${classes.filterDateField} dashboard-date-field`}
                  label={t("indicators.dateStart")}
                  type="text"
                  value={dateStart}
                  onChange={(e) => {
                    setDateStart(applyDateMask(e.target.value));
                    setActiveQuickFilter(null);
                  }}
                  onBlur={() => {
                    fetchIndicators(true);
                    fetchTodayIndicators();
                    fetchWeekIndicators();
                  }}
                  placeholder="dd/MM/yyyy"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 10 }}
                  size="small"
                  variant="outlined"
                  margin="dense"
                />
                <TextField
                  className={`${classes.filterDateField} dashboard-date-field`}
                  label={t("indicators.dateEnd")}
                  type="text"
                  value={dateEnd}
                  onChange={(e) => {
                    setDateEnd(applyDateMask(e.target.value));
                    setActiveQuickFilter(null);
                  }}
                  onBlur={() => {
                    fetchIndicators(true);
                    fetchTodayIndicators();
                    fetchWeekIndicators();
                  }}
                  placeholder="dd/MM/yyyy"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 10 }}
                  size="small"
                  variant="outlined"
                  margin="dense"
                />
              </>
            )}
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                fetchIndicators(false);
                fetchTodayIndicators();
                fetchWeekIndicators();
              }}
              disabled={indicatorsLoading}
              title={t("indicators.update")}
              aria-label={t("indicators.update")}
              className="dashboard-icon-btn-sm"
            >
              {indicatorsLoading ? (
                <CircularProgress size={18} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </div>
        )}
        {activeProfile === "cell" && (
          <div className={classes.filterBox}>
            <Button
              className={`${classes.quickFilterButton} ${
                cellManagerFilter === "today"
                  ? classes.quickFilterButtonActive
                  : ""
              }`}
              onClick={() => setCellManagerFilter("today")}
            >
              {t("indicators.today")}
            </Button>
            <Button
              className={`${classes.quickFilterButton} ${
                cellManagerFilter === "yesterday"
                  ? classes.quickFilterButtonActive
                  : ""
              }`}
              onClick={() => setCellManagerFilter("yesterday")}
            >
              {t("indicators.yesterday")}
            </Button>
            <Button
              className={`${classes.quickFilterButton} ${
                cellManagerFilter === "thisWeek"
                  ? classes.quickFilterButtonActive
                  : ""
              }`}
              onClick={() => setCellManagerFilter("thisWeek")}
            >
              {t("cellManager.thisWeek")}
            </Button>
            <Button
              className={`${classes.quickFilterButton} ${
                cellManagerFilter === "thisMonth"
                  ? classes.quickFilterButtonActive
                  : ""
              }`}
              onClick={() => setCellManagerFilter("thisMonth")}
            >
              {t("cellManager.thisMonth")}
            </Button>
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                fetchCellManagerStatistics();
                fetchManagerPendingJobs();
              }}
              disabled={cellManagerLoading}
              title={t("indicators.update")}
              aria-label={t("indicators.update")}
              className="dashboard-icon-btn-sm"
            >
              {cellManagerLoading ? (
                <CircularProgress size={18} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </div>
        )}
      </div>

      {/* Conteúdo da aba Usuário - indicadores */}
      {activeProfile === "user" && (
        <>
          {/* Banner Bem-vindo Usuário */}
          <Card className={`${classes.welcomeCard} mt-24`}>
            <div className={classes.welcomeContent}>
              <div className={classes.userInfo}>
                <Avatar className={classes.avatar}>{getUserInitials()}</Avatar>
                <div>
                  <Typography variant="h6" className="font-bold">
                    {t("welcome", { name: user?.name || "Usuário" })}
                  </Typography>
                  <Typography className={classes.welcomeSubtitle}>
                    {t("ready")}
                  </Typography>
                </div>
              </div>
              <div className="text-right">
                <Typography className={classes.welcomeDate}>
                  {formatDate(new Date(), {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Typography>
                {user && user.company_name && cell && cell.title && (
                  <Typography
                    className={`${classes.welcomeCompany} text-nowrap`}
                  >
                    {user.company_name} → {cell.title}
                  </Typography>
                )}
              </div>
            </div>
          </Card>

          <Grid container spacing={3} className="mb-32">
            {indicatorsLoading ? (
              <Grid item xs={12}>
                <Paper className={classes.indicatorsLoading}>
                  <CircularProgress size={32} />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className="ml-12"
                  >
                    {t("indicators.loading")}
                  </Typography>
                </Paper>
              </Grid>
            ) : indicatorsError ? (
              <Grid item xs={12}>
                <Paper className={classes.indicatorsError}>
                  <Typography variant="body2">{indicatorsError}</Typography>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => fetchIndicators(false)}
                    className="mt-8"
                  >
                    {t("indicators.retry")}
                  </Button>
                </Paper>
              </Grid>
            ) : indicators ? (
              <>
                <Grid item xs={false} sm={2} />
                <Grid item xs={12} sm={4}>
                  <Card className={classes.indicatorCard}>
                    <CardContent>
                      <div
                        className={`${classes.indicatorIconWrap} ${classes.indicatorIconAmber}`}
                      >
                        <FileClockIcon style={{ fontSize: 28 }} />
                      </div>
                      <Typography className={classes.indicatorValue}>
                        {indicators.pendentes_revisao ?? 0}
                      </Typography>
                      <Typography className={classes.indicatorLabel}>
                        {t("indicators.pendingReview")}
                      </Typography>
                      <Typography className={classes.indicatorSubtitle}>
                        {indicators.pendentes_revisao_24h ?? 0}{" "}
                        {t("indicators.moreThan24h")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card className={classes.indicatorCard}>
                    <CardContent>
                      <div
                        className={`${classes.indicatorIconWrap} ${classes.indicatorIconGreen}`}
                      >
                        <FileCheckIcon style={{ fontSize: 28 }} />
                      </div>
                      <Typography className={classes.indicatorValue}>
                        {indicators.finalizados ?? 0}
                      </Typography>
                      <Typography className={classes.indicatorLabel}>
                        {t("indicators.completedThisMonth")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={false} sm={2} />
              </>
            ) : null}
          </Grid>

          {/* Grid com 2 cards: Tarefas Pendentes e Progresso do Dia */}
          <Grid container spacing={3} className="mt-16">
            {/* Card Pendentes: top 3 mais antigas com prioridade por dias */}
            <Grid item xs={12} md={8}>
              <Card className={classes.pendingCard}>
                <CardContent className={classes.pendingCardContent}>
                  <div className={classes.pendingCardHeader}>
                    <div className={classes.pendingCardTitleWrapper}>
                      <AlertTriangleIcon className="dashboard-warning-icon" />
                      <Typography className={classes.pendingCardTitle}>
                        {t("indicators.pendingTasks")}
                      </Typography>
                    </div>
                    <Button
                      className={classes.createContestacaoButton}
                      onClick={() => history.push("/contestacao/nova")}
                      size="small"
                    >
                      {t("indicators.createContestation")}
                    </Button>
                  </div>
                  <div className={classes.pendingItemsWrapper}>
                    {pendingJobsLoading ? (
                      <Typography variant="body2" color="textSecondary">
                        {t("indicators.pendingLoading")}
                      </Typography>
                    ) : pendingJobs.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        {t("indicators.noPending")}
                      </Typography>
                    ) : (
                      pendingJobs.map((job) => {
                        const priority = getPriorityFromInsertion(job);
                        const chipClass =
                          priority.level === "alta"
                            ? classes.chipAlta
                            : classes.chipBaixa;
                        const priorityLabel =
                          priority.level === "alta"
                            ? t("indicators.moreThan24h")
                            : t("indicators.lessThan24h");
                        return (
                          <div
                            key={job.id}
                            className={classes.pendingItem}
                            onClick={() =>
                              history.push(
                                `/documento/revisar/contestacao/${job.id}`,
                              )
                            }
                          >
                            <div className={classes.pendingItemContent}>
                              <div className="dashboard-pending-chip-row">
                                <span
                                  className={`${classes.pendingChip} ${chipClass}`}
                                >
                                  {priorityLabel}
                                </span>
                                <Typography className={classes.pendingItemCase}>
                                  {job.nome_advogado && job.oab_advogado
                                    ? `${job.nome_advogado} - OAB: ${job.oab_advogado}`
                                    : job.nome_advogado ||
                                      job.external_id ||
                                      `#${job.id}`}
                                </Typography>
                              </div>
                              <Typography className={classes.pendingItemTitle}>
                                {job.job_name || "Sem nome"}
                              </Typography>
                              <Typography className={classes.pendingItemMeta}>
                                {t("indicators.awaitingReview")}
                                {priority.hours > 0 && ` · ${priority.hours}h`}
                              </Typography>
                            </div>
                            <ChevronRightIcon
                              className={classes.pendingChevron}
                              style={{ fontSize: 20 }}
                            />
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>

            {/* Card Progresso do Dia */}
            <Grid
              item
              xs={12}
              md={4}
              className="flex-col"
            >
              <Card className={classes.progressCard}>
                <CardContent>
                  <Typography className={classes.progressCardTitle}>
                    {t("indicators.progressToday")}
                  </Typography>

                  {/* Concluídos Hoje */}
                  <div className={classes.progressItem}>
                    <div className={classes.progressHeader}>
                      <Typography className={classes.progressLabel}>
                        {t("indicators.completedToday")}
                      </Typography>
                      <Typography className={classes.progressValue}>
                        {todayIndicators?.finalizados ?? 0}
                      </Typography>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(
                        ((todayIndicators?.finalizados ?? 0) /
                          Math.max(
                            (todayIndicators?.finalizados ?? 0) +
                              (todayIndicators?.pendentes_revisao ?? 0),
                            1,
                          )) *
                          100,
                        100,
                      )}
                      className={classes.progressBarCompleted}
                    />
                  </div>

                  {/* Aguardando Revisão */}
                  <div className={classes.progressItem}>
                    <div className={classes.progressHeader}>
                      <Typography className={classes.progressLabel}>
                        {t("indicators.awaitingReview")}
                      </Typography>
                      <Typography className={classes.progressValue}>
                        {todayIndicators?.pendentes_revisao ?? 0}
                      </Typography>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(
                        ((todayIndicators?.pendentes_revisao ?? 0) /
                          Math.max(
                            (todayIndicators?.finalizados ?? 0) +
                              (todayIndicators?.pendentes_revisao ?? 0),
                            1,
                          )) *
                          100,
                        100,
                      )}
                      className={classes.progressBarPending}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Card Estatísticas */}
              <Card className={classes.userStatsCard}>
                <CardContent>
                  <Typography className={classes.userStatsCardTitle}>
                    {t("indicators.statistics")}
                  </Typography>

                  {/* Esta semana */}
                  <div className={classes.userStatsItem}>
                    <Typography className={classes.userStatsLabel}>
                      {t("indicators.thisWeek")}
                    </Typography>
                    <Typography className={classes.userStatsValue}>
                      {weekIndicators?.finalizados ?? 0}{" "}
                      {t("indicators.pieces")}
                    </Typography>
                  </div>

                  {/* Média diária */}
                  <div className={classes.userStatsItem}>
                    <Typography className={classes.userStatsLabel}>
                      {t("indicators.dailyAverage")}
                    </Typography>
                    <Typography className={classes.userStatsValue}>
                      {weekIndicators?.finalizados
                        ? (weekIndicators.finalizados / 7).toFixed(1)
                        : "0.0"}{" "}
                      {t("indicators.pieces")}
                    </Typography>
                  </div>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Card de Produtividade dos Últimos 30 Dias - Usuário */}
          <Grid container spacing={3} className="mt-16">
            <Grid item xs={12}>
              <Card className={classes.productivityCard}>
                <CardContent>
                  <Typography className={classes.productivityCardTitle}>
                    {t("indicators.createdVsCompleted") ||
                      "Criadas x Concluídas - Últimos 30 Dias"}
                  </Typography>

                  {dailyProductivityLoading ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height={200}
                    >
                      <CircularProgress size={32} />
                    </Box>
                  ) : (
                    <>
                      <div className={classes.chartContainer}>
                        {dailyProductivity.map((day, index) => {
                          const maxValue = Math.max(
                            ...dailyProductivity.map((d) =>
                              Math.max(d.completed, d.created || 0),
                            ),
                            1,
                          );

                          // Calcula alturas para created e completed
                          const minHeightPx = 4;
                          const containerHeight = 200;

                          const completedHeightPercent =
                            day.completed === 0
                              ? 0
                              : (day.completed / maxValue) * 100;
                          const completedHeightPx =
                            day.completed === 0
                              ? 0
                              : Math.max(
                                  (completedHeightPercent / 100) *
                                    containerHeight,
                                  minHeightPx,
                                );

                          const createdHeightPercent =
                            (day.created || 0) === 0
                              ? 0
                              : ((day.created || 0) / maxValue) * 100;
                          const createdHeightPx =
                            (day.created || 0) === 0
                              ? 0
                              : Math.max(
                                  (createdHeightPercent / 100) *
                                    containerHeight,
                                  minHeightPx,
                                );

                          const date = new Date(day.date);
                          const dayLabel = String(date.getDate()).padStart(
                            2,
                            "0",
                          );
                          const monthLabel = String(
                            date.getMonth() + 1,
                          ).padStart(2, "0");
                          const fullLabel = `${dayLabel}/${monthLabel}`;
                          const isToday =
                            day.date ===
                            `${new Date().getFullYear()}-${String(
                              new Date().getMonth() + 1,
                            ).padStart(2, "0")}-${String(
                              new Date().getDate(),
                            ).padStart(2, "0")}`;

                          return (
                            <div
                              key={day.date}
                              className={classes.barWrapper}
                              title={`${fullLabel}: ${day.created || 0} criadas, ${day.completed} concluídas`}
                            >
                              <div className="dashboard-chart-bar-group">
                                <div
                                  className="dashboard-chart-bar"
                                  style={{
                                    height: `${createdHeightPx}px`,
                                    backgroundColor: "#3b82f6",
                                    opacity:
                                      (day.created || 0) === 0 ? 0.25 : 1,
                                  }}
                                />
                                <div
                                  className="dashboard-chart-bar"
                                  style={{
                                    height: `${completedHeightPx}px`,
                                    backgroundColor: isToday
                                      ? "#f59e0b"
                                      : "#10b981",
                                    opacity: day.completed === 0 ? 0.25 : 1,
                                  }}
                                />
                              </div>
                              <Typography
                                className={classes.barLabel}
                                style={
                                  isToday
                                    ? { fontWeight: 600, color: "#f59e0b" }
                                    : undefined
                                }
                              >
                                {fullLabel}
                              </Typography>
                            </div>
                          );
                        })}
                      </div>

                      <div className={classes.chartLegend}>
                        <div className={classes.legendItem}>
                          <div className="dashboard-legend-row">
                            <div
                              className="dashboard-legend-dot"
                              style={{ backgroundColor: "#3b82f6" }}
                            />
                            <Typography className={classes.legendLabel}>
                              {t("indicators.created") || "Criadas"}
                            </Typography>
                          </div>
                          <Typography className={classes.legendValue}>
                            {dailyProductivity.reduce(
                              (sum, day) => sum + (day.created || 0),
                              0,
                            )}
                          </Typography>
                        </div>
                        <div className={classes.legendItem}>
                          <div className="dashboard-legend-row">
                            <div
                              className="dashboard-legend-dot"
                              style={{ backgroundColor: "#10b981" }}
                            />
                            <Typography className={classes.legendLabel}>
                              {t("indicators.completed") || "Concluídas"}
                            </Typography>
                          </div>
                          <Typography className={classes.legendValue}>
                            {dailyProductivity.reduce(
                              (sum, day) => sum + day.completed,
                              0,
                            )}
                          </Typography>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {activeProfile === "cell" && (
        <>
          {/* Banner Bem-vindo Gestor de Célula */}
          <Card className={`${classes.welcomeCard} mt-24`}>
            <div className={classes.welcomeContent}>
              <div className={classes.userInfo}>
                <Avatar className={classes.avatar}>{getUserInitials()}</Avatar>
                <div>
                  <Typography variant="h6" className="font-bold">
                    {t("welcome", { name: user?.name || "Usuário" })}
                  </Typography>
                  <Typography className={classes.welcomeSubtitle}>
                    {t("cellManager.subtitle")}
                    {cell && cell.title && ` ${cell.title}`}
                  </Typography>
                </div>
              </div>
              <div className="text-right">
                <Typography className={classes.welcomeDate}>
                  {cellManagerStats?.team_members || 0}{" "}
                  {t("cellManager.activeMembers")} •{" "}
                  {cellManagerStats?.total_pending || 0}{" "}
                  {t("cellManager.tasksInProgress")}
                </Typography>
              </div>
            </div>
          </Card>

          {/* Indicadores do Gestor de Célula */}
          {cellManagerLoading ? (
            <Paper className="dashboard-loading-state">
              <CircularProgress />
              <Typography
                className="mt-16"
                variant="body2"
                color="textSecondary"
              >
                {t("cellManager.loading")}
              </Typography>
            </Paper>
          ) : cellManagerError ? (
            <Paper className="dashboard-loading-state">
              <Typography color="error">{t("cellManager.error")}</Typography>
            </Paper>
          ) : cellManagerStats ? (
            <>
              {/* Cards de Indicadores */}
              <Grid container spacing={3}>
                <Grid item xs={false} sm={3} />
                <Grid item xs={12} sm={3}>
                  <Card className={classes.indicatorCard}>
                    <CardContent>
                      <div
                        className={`${classes.indicatorIconWrap} ${classes.indicatorIconAmber}`}
                      >
                        <FileClockIcon style={{ fontSize: 28 }} />
                      </div>
                      <Typography className={classes.indicatorValue}>
                        {cellManagerStats.cell_totals.pending ?? 0}
                      </Typography>
                      <Typography className={classes.indicatorLabel}>
                        {t("indicators.pendingReview")}
                      </Typography>
                      <Typography className={classes.indicatorSubtitle}>
                        {cellManagerStats.cell_totals.pending_24h ?? 0}{" "}
                        {t("indicators.moreThan24h")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Card className={classes.indicatorCard}>
                    <CardContent>
                      <div
                        className={`${classes.indicatorIconWrap} ${classes.indicatorIconGreen}`}
                      >
                        <FileCheckIcon style={{ fontSize: 28 }} />
                      </div>
                      <Typography className={classes.indicatorValue}>
                        {cellManagerStats.cell_totals.completed_month ?? 0}
                      </Typography>
                      <Typography className={classes.indicatorLabel}>
                        {t("indicators.completedThisMonth")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={false} sm={3} />
              </Grid>

              {/* Grid com Performance da Equipe e Tarefas do Gestor */}
              <Grid container spacing={3} className="mt-16">
                {/* Tabela de Performance da Equipe */}
                <Grid item xs={12} md={8} style={{ display: "flex" }}>
                  <Card className="w-full flex-col">
                    <CardContent className="flex-1 flex-col">
                      <Typography
                        variant="h6"
                        className="mb-16 font-semibold"
                      >
                        {t("cellManager.teamPerformance")}
                      </Typography>

                      {cellManagerStats.team_performance.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                          {t("cellManager.noMembers")}
                        </Typography>
                      ) : (
                        <div className="flex-1 overflow-y-auto">
                          <table className={classes.cellManagerTable}>
                            <thead className={classes.cellManagerTableHead}>
                              <tr>
                                <th>{t("cellManager.member")}</th>
                                <th className="text-center">
                                  {t("indicators.pendingReview")}
                                </th>
                                <th className="text-center">
                                  {t("indicators.completed")}
                                </th>
                              </tr>
                            </thead>
                            <tbody className={classes.cellManagerTableBody}>
                              {cellManagerStats.team_performance.map(
                                (member) => {
                                  const initials = member.user_name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .slice(0, 2)
                                    .join("")
                                    .toUpperCase();

                                  return (
                                    <tr key={member.user_id}>
                                      <td>
                                        <div
                                          className={
                                            classes.cellManagerUserInfo
                                          }
                                        >
                                          <Avatar
                                            className={
                                              classes.cellManagerUserAvatar
                                            }
                                          >
                                            {initials}
                                          </Avatar>
                                          <span
                                            className={
                                              classes.cellManagerUserName
                                            }
                                          >
                                            {member.user_name}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="text-center">
                                        <span
                                          className={`${
                                            classes.cellManagerBadge
                                          } ${
                                            member.pending > 3
                                              ? classes.cellManagerBadgeHigh
                                              : classes.cellManagerBadgeNormal
                                          }`}
                                        >
                                          {member.pending}
                                        </span>
                                      </td>
                                      <td className="text-center font-medium">
                                        {member.completed}
                                      </td>
                                    </tr>
                                  );
                                },
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Card de Tarefas do Gestor */}
                <Grid item xs={12} md={4} style={{ display: "flex" }}>
                  <Card className={`${classes.pendingCard} w-full`}>
                    <CardContent className={classes.pendingCardContent}>
                      <div className={classes.pendingCardHeader}>
                        <div className={classes.pendingCardTitleWrapper}>
                          <AlertTriangleIcon className="dashboard-warning-icon" />
                          <Typography className={classes.pendingCardTitle}>
                            {t("cellManager.myTasks")}
                          </Typography>
                        </div>
                        <Button
                          className={classes.createContestacaoButton}
                          onClick={() => history.push("/contestacao/nova")}
                          size="small"
                        >
                          {t("cellManager.createContestation")}
                        </Button>
                      </div>
                      <div className={classes.pendingItemsWrapper}>
                        {managerPendingLoading ? (
                          <Typography variant="body2" color="textSecondary">
                            {t("indicators.pendingLoading")}
                          </Typography>
                        ) : managerPendingJobs.length === 0 ? (
                          <Typography variant="body2" color="textSecondary">
                            {t("indicators.noPending")}
                          </Typography>
                        ) : (
                          managerPendingJobs.map((job) => {
                            const priority = getPriorityFromInsertion(job);
                            const chipClass =
                              priority.level === "alta"
                                ? classes.chipAlta
                                : classes.chipBaixa;
                            const priorityLabel =
                              priority.level === "alta"
                                ? `${t("indicators.pendingReview")} ${t("indicators.moreThan24h")}`
                                : t("indicators.pendingReview");
                            return (
                              <div
                                key={job.id}
                                className={classes.pendingItem}
                                onClick={() =>
                                  history.push(
                                    `/documento/revisar/contestacao/${job.id}`,
                                  )
                                }
                              >
                                <div className={classes.pendingItemContent}>
                                  <div className="dashboard-pending-chip-row">
                                    <span
                                      className={`${classes.pendingChip} ${chipClass}`}
                                    >
                                      {priorityLabel}
                                    </span>
                                    <Typography
                                      className={classes.pendingItemCase}
                                    >
                                      {job.nome_advogado && job.oab_advogado
                                        ? `${job.nome_advogado} - OAB: ${job.oab_advogado}`
                                        : job.nome_advogado ||
                                          job.external_id ||
                                          `#${job.id}`}
                                    </Typography>
                                  </div>
                                  <Typography
                                    className={classes.pendingItemTitle}
                                  >
                                    {job.job_name || "Sem nome"}
                                  </Typography>
                                  <Typography
                                    className={classes.pendingItemMeta}
                                  >
                                    {t("indicators.awaitingReview")}
                                    {priority.hours > 0 &&
                                      ` · ${priority.hours}h`}
                                  </Typography>
                                </div>
                                <ChevronRightIcon
                                  className={classes.pendingChevron}
                                  style={{ fontSize: 20 }}
                                />
                              </div>
                            );
                          })
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Card de Produtividade dos Últimos 30 Dias */}
              <Grid container spacing={3} className="mt-16">
                <Grid item xs={12}>
                  <Card className={classes.productivityCard}>
                    <CardContent>
                      <Typography className={classes.productivityCardTitle}>
                        {t("indicators.createdVsCompleted") ||
                          "Criadas x Concluídas - Últimos 30 Dias"}
                      </Typography>

                      {dailyProductivityLoading ? (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height={200}
                        >
                          <CircularProgress size={32} />
                        </Box>
                      ) : (
                        <>
                          <div className={classes.chartContainer}>
                            {dailyProductivity.map((day, index) => {
                              const maxValue = Math.max(
                                ...dailyProductivity.map((d) =>
                                  Math.max(d.completed, d.created || 0),
                                ),
                                1,
                              );

                              // Calcula alturas para created e completed
                              const minHeightPx = 4;
                              const containerHeight = 200;

                              const completedHeightPercent =
                                day.completed === 0
                                  ? 0
                                  : (day.completed / maxValue) * 100;
                              const completedHeightPx =
                                day.completed === 0
                                  ? 0
                                  : Math.max(
                                      (completedHeightPercent / 100) *
                                        containerHeight,
                                      minHeightPx,
                                    );

                              const createdHeightPercent =
                                (day.created || 0) === 0
                                  ? 0
                                  : ((day.created || 0) / maxValue) * 100;
                              const createdHeightPx =
                                (day.created || 0) === 0
                                  ? 0
                                  : Math.max(
                                      (createdHeightPercent / 100) *
                                        containerHeight,
                                      minHeightPx,
                                    );

                              const date = new Date(day.date);
                              const dayLabel = String(date.getDate()).padStart(
                                2,
                                "0",
                              );
                              const monthLabel = String(
                                date.getMonth() + 1,
                              ).padStart(2, "0");
                              const fullLabel = `${dayLabel}/${monthLabel}`;
                              const isToday =
                                day.date ===
                                `${new Date().getFullYear()}-${String(
                                  new Date().getMonth() + 1,
                                ).padStart(2, "0")}-${String(
                                  new Date().getDate(),
                                ).padStart(2, "0")}`;

                              return (
                                <div
                                  key={day.date}
                                  className={classes.barWrapper}
                                  title={`${fullLabel}: ${day.created || 0} criadas, ${day.completed} concluídas`}
                                >
                                  <div className="dashboard-chart-bar-group">
                                    <div
                                      className="dashboard-chart-bar"
                                      style={{
                                        height: `${createdHeightPx}px`,
                                        backgroundColor: "#3b82f6",
                                        opacity:
                                          (day.created || 0) === 0 ? 0.25 : 1,
                                      }}
                                    />
                                    <div
                                      className="dashboard-chart-bar"
                                      style={{
                                        height: `${completedHeightPx}px`,
                                        backgroundColor: isToday
                                          ? "#f59e0b"
                                          : "#10b981",
                                        opacity: day.completed === 0 ? 0.25 : 1,
                                      }}
                                    />
                                  </div>
                                  <Typography
                                    className={classes.barLabel}
                                    style={
                                      isToday
                                        ? { fontWeight: 600, color: "#f59e0b" }
                                        : undefined
                                    }
                                  >
                                    {fullLabel}
                                  </Typography>
                                </div>
                              );
                            })}
                          </div>

                          <div className={classes.chartLegend}>
                            <div className={classes.legendItem}>
                              <div className="dashboard-legend-row">
                                <div
                                  className="dashboard-legend-dot"
                                  style={{ backgroundColor: "#3b82f6" }}
                                />
                                <Typography className={classes.legendLabel}>
                                  {t("indicators.created") || "Criadas"}
                                </Typography>
                              </div>
                              <Typography className={classes.legendValue}>
                                {dailyProductivity.reduce(
                                  (sum, day) => sum + (day.created || 0),
                                  0,
                                )}
                              </Typography>
                            </div>
                            <div className={classes.legendItem}>
                              <div className="dashboard-legend-row">
                                <div
                                  className="dashboard-legend-dot"
                                  style={{ backgroundColor: "#10b981" }}
                                />
                                <Typography className={classes.legendLabel}>
                                  {t("indicators.completed") || "Concluídas"}
                                </Typography>
                              </div>
                              <Typography className={classes.legendValue}>
                                {dailyProductivity.reduce(
                                  (sum, day) => sum + day.completed,
                                  0,
                                )}
                              </Typography>
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          ) : null}
        </>
      )}

      {activeProfile === "office" && (
        <>
          {/* Banner Bem-vindo Gestor de Escritório */}
          <Card className={`${classes.welcomeCard} mt-24`}>
            <div className={classes.welcomeContent}>
              <div className={classes.userInfo}>
                <Avatar className={classes.avatar}>
                  <Building2Icon style={{ fontSize: 28 }} />
                </Avatar>
                <div>
                  <Typography variant="h6" className="font-bold">
                    {user?.company_name || t("officeManager.title")}
                  </Typography>
                  <Typography className={classes.welcomeSubtitle}>
                    {officeManagerStats?.totals?.total_cells || 0}{" "}
                    {t("officeManager.cells")} •{" "}
                    {officeManagerStats?.totals?.active_lawyers || 0}{" "}
                    {t("officeManager.lawyers")} •{" "}
                    {officeManagerStats?.totals?.in_progress || 0}{" "}
                    {t("officeManager.activeProcesses")}
                  </Typography>
                </div>
              </div>
            </div>
          </Card>

          {/* Indicadores do Gestor de Escritório */}
          {officeManagerLoading ? (
            <Paper className="dashboard-loading-state">
              <CircularProgress />
              <Typography
                className="mt-16"
                variant="body2"
                color="textSecondary"
              >
                {t("officeManager.loading")}
              </Typography>
            </Paper>
          ) : officeManagerError ? (
            <Paper className="dashboard-loading-state">
              <Typography color="error">{t("officeManager.error")}</Typography>
            </Paper>
          ) : officeManagerStats ? (
            <>
              {/* Cards de Indicadores */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <Card className={classes.indicatorCard}>
                    <CardContent>
                      <div
                        className={`${classes.indicatorIconWrap} ${classes.indicatorIconPurple}`}
                      >
                        <LayersIcon style={{ fontSize: 28 }} />
                      </div>
                      <Typography className={classes.indicatorValue}>
                        {officeManagerStats.totals.total_cells}
                      </Typography>
                      <Typography className={classes.indicatorLabel}>
                        {t("officeManager.totalCells")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Card className={classes.indicatorCard}>
                    <CardContent>
                      <div
                        className={`${classes.indicatorIconWrap} ${classes.indicatorIconPurple}`}
                      >
                        <Edit3Icon style={{ fontSize: 28 }} />
                      </div>
                      <Typography className={classes.indicatorValue}>
                        {officeManagerStats.totals.active_lawyers}
                      </Typography>
                      <Typography className={classes.indicatorLabel}>
                        {t("officeManager.activeLawyers")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Card className={classes.indicatorCard}>
                    <CardContent>
                      <div
                        className={`${classes.indicatorIconWrap} ${classes.indicatorIconAmber}`}
                      >
                        <FileClockIcon style={{ fontSize: 28 }} />
                      </div>
                      <Typography className={classes.indicatorValue}>
                        {officeManagerStats.totals.in_progress}
                      </Typography>
                      <Typography className={classes.indicatorLabel}>
                        {t("indicators.pendingReview")}
                      </Typography>
                      <Typography className={classes.indicatorSubtitle}>
                        {officeManagerStats.totals.pending_24h || 0}{" "}
                        {t("indicators.moreThan24h")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Card className={classes.indicatorCard}>
                    <CardContent>
                      <div
                        className={`${classes.indicatorIconWrap} ${classes.indicatorIconGreen}`}
                      >
                        <FileCheckIcon style={{ fontSize: 28 }} />
                      </div>
                      <Typography className={classes.indicatorValue}>
                        {officeManagerStats.totals.completed_month}
                      </Typography>
                      <Typography className={classes.indicatorLabel}>
                        {t("indicators.completedThisMonth")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Performance da Equipe e Volume por Cliente */}
              <Grid container spacing={3} className="mt-16">
                {/* Performance da Equipe */}
                <Grid item xs={12} md={8}>
                  <Card className="h-full">
                    <CardContent>
                      <div className="flex-row-between mb-16">
                        <Typography variant="h6" className="font-semibold">
                          {t("officeManager.teamPerformance") ||
                            "Performance da Equipe"}
                        </Typography>
                        <div className="flex-row gap-12">
                          {/* Filtro de período */}
                          <select
                            value={officeManagerFilter}
                            onChange={(e) =>
                              setOfficeManagerFilter(e.target.value)
                            }
                            className="dashboard-office-select"
                          >
                            <option value="today">
                              {t("indicators.today")}
                            </option>
                            <option value="yesterday">
                              {t("indicators.yesterday")}
                            </option>
                            <option value="thisWeek">
                              {t("indicators.thisWeek")}
                            </option>
                            <option value="thisMonth">
                              {t("indicators.thisMonth") || "Este mês"}
                            </option>
                          </select>
                          {/* Toggle célula atual */}
                          <div className="dashboard-toggle-group">
                            <button
                              onClick={() => setOfficeManagerCellFilter(false)}
                              className={`dashboard-toggle-btn ${!officeManagerCellFilter ? "dashboard-toggle-btn-active" : ""}`}
                            >
                              {t("officeManager.allCells") || "Todas"}
                            </button>
                            <button
                              onClick={() => setOfficeManagerCellFilter(true)}
                              className={`dashboard-toggle-btn dashboard-toggle-btn-divider ${officeManagerCellFilter ? "dashboard-toggle-btn-active" : ""}`}
                            >
                              {t("officeManager.currentCell") || "Célula Atual"}
                            </button>
                          </div>
                        </div>
                      </div>

                      {!officeManagerStats?.users_performance ||
                      officeManagerStats.users_performance.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                          {t("officeManager.noUsers") ||
                            "Nenhum usuário encontrado"}
                        </Typography>
                      ) : (
                        <table className={classes.cellManagerTable}>
                          <thead className={classes.cellManagerTableHead}>
                            <tr>
                              <th>{t("cellManager.member")}</th>
                              <th className="text-center">
                                {t("indicators.pendingReview")}
                              </th>
                              <th className="text-center">
                                {t("indicators.completed")}
                              </th>
                            </tr>
                          </thead>
                          <tbody className={classes.cellManagerTableBody}>
                            {officeManagerStats.users_performance.map(
                              (user) => {
                                const initials = user.user_name
                                  .split(" ")
                                  .map((n) => n[0])
                                  .slice(0, 2)
                                  .join("")
                                  .toUpperCase();

                                return (
                                  <tr key={user.user_id}>
                                    <td>
                                      <div
                                        className={classes.cellManagerUserInfo}
                                      >
                                        <Avatar
                                          className={
                                            classes.cellManagerUserAvatar
                                          }
                                        >
                                          {initials}
                                        </Avatar>
                                        <div>
                                          <span
                                            className={
                                              classes.cellManagerUserName
                                            }
                                          >
                                            {user.user_name}
                                          </span>
                                          <div className="dashboard-cell-tags">
                                            {user.cells.map((cell) => (
                                              <span
                                                key={cell.id}
                                                className="dashboard-cell-tag"
                                              >
                                                {cell.name}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="text-center">
                                      <span
                                        className={`${classes.cellManagerBadge} ${
                                          user.pending > 3
                                            ? classes.cellManagerBadgeHigh
                                            : classes.cellManagerBadgeNormal
                                        }`}
                                      >
                                        {user.pending}
                                      </span>
                                    </td>
                                    <td className="text-center font-medium">
                                      {user.completed}
                                    </td>
                                  </tr>
                                );
                              },
                            )}
                          </tbody>
                        </table>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Volume por Cliente */}
                <Grid item xs={12} md={4}>
                  <Card className="h-full">
                    <CardContent>
                      <Typography
                        variant="h6"
                        className="dashboard-volume-title"
                      >
                        {t("officeManager.volumeByClient")}
                      </Typography>

                      {/* Gráfico de Pizza */}
                      <Box className="dashboard-donut-center">
                        <DonutChart
                          data={(officeManagerStats.clients || []).map((c) => {
                            console.log(
                              "Cliente:",
                              c.name,
                              "Volume:",
                              c.volume,
                              "Cor:",
                              c.color,
                            );
                            return {
                              value: c.volume || 0,
                              color: c.color,
                            };
                          })}
                          size={140}
                        />
                      </Box>

                      {/* Legenda */}
                      <div className="mt-16">
                        {(officeManagerStats.clients || []).map(
                          (client, index) => (
                            <div
                              key={index}
                              className={classes.clientLegendItem}
                            >
                              <div className="flex-row">
                                <div
                                  className={classes.clientLegendColor}
                                  style={{ backgroundColor: client.color }}
                                />
                                <Typography className="text-sm">
                                  {client.name}
                                </Typography>
                                <Typography className="text-xs text-muted ml-4">
                                  ({client.cells}{" "}
                                  {client.cells === 1
                                    ? t("officeManager.cell")
                                    : t("officeManager.cells")}
                                  )
                                </Typography>
                              </div>
                              <Typography className="text-sm font-medium">
                                {client.volume}
                              </Typography>
                            </div>
                          ),
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          ) : null}
        </>
      )}
    </div>
  );
};

export default Dashboard;
